import {HttpClient, HttpContext, HttpErrorResponse} from '@angular/common/http';
import {authLocalErrorCodes} from './auth-local-error-codes';
import {LOCALLY_HANDLED_API_ERROR_CODES, wasSystemApiErrorPresented} from '../shared/errors/local-api-error';
import {isPushSupported} from '../push/push-native';
import {PushLocalStateService} from '../push/push-local-state.service';
import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, Observable, catchError, firstValueFrom, from, map, of, shareReplay, switchMap, tap, throwError} from 'rxjs';
import {environment} from '../../environments/environment';
import {AppLanguageService} from '../shared/i18n/app-language.service';
import {normalizeAppLanguageCode} from '../shared/i18n/app-language';
import {buildApiUrl} from '../shared/api/api-url';
import {isNativeCapacitorPlatform} from '../shared/platform';
import {RegionalPreferencesService} from '../shared/settings/regional-preferences.service';
import type {
  ApiResponse,
  CurrentUserResponse,
  CreateDemoDataResponse,
  DeleteMyAccountResponse,
  AuthSession,
  ForgotPasswordRequestDto,
  ForgotPasswordResponseDto,
  GoogleLoginRequest,
  GoogleLoginApiRequest,
  AuthClientTypeDto,
  LoginRequest,
  LoginApiRequest,
  LoginResponse,
  LogoutRequestDto,
  RefreshTokenRequestDto,
  RegisterRequest,
  RegisterResponse,
  ResetMyWorkspaceResponse,
  ResetPasswordRequestDto,
  ResetPasswordResponseDto,
  VerifyEmailRequestDto,
  VerifyEmailResponseDto,
  UpdateMyPreferencesRequest,
  UpdateMyPreferencesResponse,
} from './auth.models';
import {AuthRefreshTokenStorage} from './auth-refresh-token.storage';
import {AndroidVersionObservationService} from '../shared/app-package-info/android-version-observation.service';
import {
  clearStoredAuthSession,
  isAuthSessionActive,
  readStoredAuthSession,
  writeStoredAuthSession,
} from './auth.storage';

const JSON_CONTENT_TYPE = 'application/json';
const LOGIN_PATH = '/v1/auth/login';
const REGISTER_PATH = '/v1/auth/register';
const GOOGLE_LOGIN_PATH = '/v1/auth/google';
const REFRESH_PATH = '/v1/auth/refresh';
const LOGOUT_PATH = '/v1/auth/logout';
const VERIFY_EMAIL_PATH = '/v1/auth/verify-email';
const FORGOT_PASSWORD_PATH = '/v1/auth/forgot-password';
const RESET_PASSWORD_PATH = '/v1/auth/reset-password';
const CURRENT_USER_PATH = '/v1/auth/me';
const CREATE_DEMO_DATA_PATH = '/v1/auth/me/demo-data/create';
const RESET_WORKSPACE_PATH = '/v1/auth/me/workspace';
const UPDATE_MY_PREFERENCES_PATH = '/v1/auth/me/preferences';
const ACCESS_TOKEN_REFRESH_SAFETY_WINDOW_MS = 60_000;
const DEFAULT_REFRESH_COOLDOWN_SECONDS = 60;
const MAX_REFRESH_COOLDOWN_SECONDS = 300;

export interface AuthRequestDiagnostics {
  readonly method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  readonly requestUrl: string;
  readonly responseUrl?: string;
  readonly statusText?: string;
  readonly httpMessage?: string;
  readonly retryAfterSeconds?: number;
}

export class AuthError extends Error {
  readonly retryAfterSeconds?: number;

  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: Record<string, string[]>,
    public readonly diagnostics?: AuthRequestDiagnostics,
  ) {
    super(message);
    this.name = 'AuthError';
    this.retryAfterSeconds = diagnostics?.retryAfterSeconds;
  }
}

function buildAccessTokenRefreshAtEpochMs(expiresInSeconds: number, receivedAtEpochMs = Date.now()): number | undefined {
  if (!Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) {
    return undefined;
  }

  const lifetimeMs = expiresInSeconds * 1000;
  if (!Number.isSafeInteger(lifetimeMs)) {
    return undefined;
  }

  const safetyWindowMs = Math.min(ACCESS_TOKEN_REFRESH_SAFETY_WINDOW_MS, lifetimeMs * 0.1);
  return receivedAtEpochMs + lifetimeMs - safetyWindowMs;
}

function normalizeSessionTiming(session: AuthSession): AuthSession {
  if (Number.isFinite(session.accessTokenRefreshAtEpochMs)) {
    return session;
  }

  const accessTokenRefreshAtEpochMs = session.expiresInSeconds === undefined
    ? undefined
    : buildAccessTokenRefreshAtEpochMs(session.expiresInSeconds);

  return accessTokenRefreshAtEpochMs === undefined
    ? session
    : {...session, accessTokenRefreshAtEpochMs};
}

function readRetryAfterSeconds(error: HttpErrorResponse): number | undefined {
  const rawRetryAfter = error.headers.get('Retry-After')?.trim();
  if (!rawRetryAfter) {
    return undefined;
  }

  const delaySeconds = Number(rawRetryAfter);
  if (Number.isFinite(delaySeconds) && delaySeconds >= 0) {
    return Math.ceil(delaySeconds);
  }

  const retryAt = Date.parse(rawRetryAfter);
  const serverDate = Date.parse(error.headers.get('Date') ?? '');
  if (!Number.isFinite(retryAt) || !Number.isFinite(serverDate) || retryAt < serverDate) {
    return undefined;
  }

  return Math.ceil((retryAt - serverDate) / 1000);
}

function normalizeDetailKey(key: string): string {
  if (!key.length) {
    return key;
  }

  return `${key[0].toLowerCase()}${key.slice(1)}`;
}

function extractDetails(payload: ApiResponse<unknown> | null): Record<string, string[]> | undefined {
  const details = payload?.error?.details;
  if (!details) {
    return undefined;
  }

  const normalized: Record<string, string[]> = {};
  for (const [rawKey, messages] of Object.entries(details)) {
    if (!Array.isArray(messages) || messages.length === 0) {
      continue;
    }

    normalized[normalizeDetailKey(rawKey)] = messages;
  }

  return normalized;
}

function toAuthSession(response: LoginResponse): AuthSession {
  return {
    accessToken: response.accessToken,
    expiresAt: response.expiresAt,
    expiresInSeconds: response.expiresInSeconds,
    refreshToken: response.refreshToken ?? undefined,
    refreshTokenExpiresAt: response.refreshTokenExpiresAt,
    user: {
      id: response.id,
      email: response.email,
      username: response.username,
    },
  };
}

export function createGoogleLoginTraceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `google-login-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly sessionSubject = new BehaviorSubject<AuthSession | null>(readStoredAuthSession());
  private refreshPromise: Promise<AuthSession> | null = null;
  private refreshCooldownUntilMonotonicMs = 0;
  private preferencesSynchronization: {
    sessionKey: string;
    result: Observable<CurrentUserResponse | null>;
  } | null = null;

  readonly session$ = this.sessionSubject.asObservable();

  constructor(
    private readonly httpClient: HttpClient,
    private readonly appLanguageService: AppLanguageService,
    private readonly translateService: TranslateService,
    private readonly refreshTokenStorage: AuthRefreshTokenStorage,
    private readonly regionalPreferencesService: RegionalPreferencesService,
    private readonly androidVersionObservation: AndroidVersionObservationService,
    private readonly pushLocalState: PushLocalStateService,
  ) {
    void this.androidVersionObservation.observe(this.sessionSubject.value);
  }

  get session(): AuthSession | null {
    return this.sessionSubject.value;
  }

  isAuthenticated(): boolean {
    return isAuthSessionActive(this.session);
  }

  login(request: LoginRequest): Observable<AuthSession> {
    const apiRequest: LoginApiRequest = {
      ...request,
      ...this.clientHistoryMetadata(),
      clientType: this.resolveClientType(),
    };
    return this.postAuth<LoginResponse>(LOGIN_PATH, apiRequest).pipe(
      switchMap((payload) => this.requireSuccess(payload, 200)),
      switchMap((response) => this.storeSessionAndSynchronizePreferences(response)),
    );
  }

  register(request: RegisterRequest): Observable<RegisterResponse> {
    return this.postAuth<RegisterResponse>(REGISTER_PATH, request).pipe(
      switchMap((payload) => this.requireSuccess(payload, 200)),
    );
  }

  verifyEmail(request: VerifyEmailRequestDto): Observable<VerifyEmailResponseDto> {
    return this.postAuth<VerifyEmailResponseDto>(VERIFY_EMAIL_PATH, request).pipe(
      switchMap((payload) => this.requireSuccess(payload, 200)),
    );
  }

  forgotPassword(request: ForgotPasswordRequestDto): Observable<ForgotPasswordResponseDto> {
    return this.postAuth<ForgotPasswordResponseDto>(FORGOT_PASSWORD_PATH, request).pipe(
      switchMap((payload) => this.requireSuccess(payload, 200)),
    );
  }

  resetPassword(request: ResetPasswordRequestDto): Observable<ResetPasswordResponseDto> {
    return this.postAuth<ResetPasswordResponseDto>(RESET_PASSWORD_PATH, request).pipe(
      switchMap((payload) => this.requireSuccess(payload, 200)),
    );
  }

  loginWithGoogle(request: GoogleLoginRequest): Observable<AuthSession> {
    const apiRequest: GoogleLoginApiRequest = {
      ...request,
      ...this.clientHistoryMetadata(),
      clientType: this.resolveClientType(),
    };
    return this.postAuth<LoginResponse>(GOOGLE_LOGIN_PATH, apiRequest).pipe(
      switchMap((payload) => this.requireSuccess(payload, 200)),
      switchMap((response) => this.storeSessionAndSynchronizePreferences(response)),
    );
  }

  getCurrentUser(): Observable<CurrentUserResponse> {
    return this.getAuth<CurrentUserResponse>(CURRENT_USER_PATH).pipe(
      switchMap((payload) => this.requireSuccess(payload, 200)),
    );
  }

  updateMyPreferences(request: UpdateMyPreferencesRequest): Observable<UpdateMyPreferencesResponse> {
    if (!this.session) {
      return throwError(() => new AuthError('Authentication session is not available.', 401));
    }

    return this.putAuth<UpdateMyPreferencesResponse>(UPDATE_MY_PREFERENCES_PATH, request).pipe(
      switchMap((payload) => this.requireSuccess(payload, 200)),
    );
  }

  createDemoData(): Observable<CreateDemoDataResponse> {
    return this.postAuth<CreateDemoDataResponse>(CREATE_DEMO_DATA_PATH, {}).pipe(
      switchMap((payload) => this.requireSuccess(payload, 200)),
    );
  }

  resetMyWorkspace(): Observable<ResetMyWorkspaceResponse> {
    return this.deleteAuth<ResetMyWorkspaceResponse>(RESET_WORKSPACE_PATH).pipe(
      switchMap((payload) => this.requireSuccess(payload, 200)),
    );
  }

  deleteMyAccount(): Observable<DeleteMyAccountResponse> {
    return (isPushSupported() ? from(this.pushLocalState.disable(this.session?.user.id)) : of(undefined)).pipe(
      switchMap(() => this.deleteAuth<DeleteMyAccountResponse>(CURRENT_USER_PATH)),
      switchMap((payload) => this.requireSuccess(payload, 200)),
      tap((response) => {
        if (response.deleted) {
          this.clearSession();
        }
      }),
    );
  }

  async setSession(session: AuthSession): Promise<void> {
    const persistedSession = await this.persistSession(session);
    await firstValueFrom(this.synchronizeCurrentUserPreferences(persistedSession));
  }

  clearSession(): void {
    clearStoredAuthSession();
    this.preferencesSynchronization = null;
    this.refreshCooldownUntilMonotonicMs = 0;
    this.sessionSubject.next(null);
    void this.refreshTokenStorage.clear().catch(() => undefined);
  }

  async logout(): Promise<void> {
    await this.pushLocalState.disable(this.session?.user.id);
    const refreshToken = await this.readNativeRefreshTokenSafely();
    const request: LogoutRequestDto = {refreshToken};
    try {
      await firstValueFrom(this.httpClient.post<void>(buildApiUrl(LOGOUT_PATH), request, {
        headers: this.buildJsonHeaders(),
        withCredentials: true,
      }));
    } catch {
      // Local logout still succeeds while offline; the server session expires independently.
    } finally {
      clearStoredAuthSession();
      this.preferencesSynchronization = null;
      this.refreshCooldownUntilMonotonicMs = 0;
      this.sessionSubject.next(null);
      await this.refreshTokenStorage.clear().catch(() => undefined);
    }
  }

  shouldRefreshAccessToken(): boolean {
    const session = this.session;
    if (!session || !Number.isFinite(session.accessTokenRefreshAtEpochMs)) {
      return false;
    }

    return Date.now() >= session.accessTokenRefreshAtEpochMs!;
  }

  async ensureActiveSession(): Promise<boolean> {
    if (!this.session) {
      return false;
    }

    if (!this.shouldRefreshAccessToken()) {
      return true;
    }

    try {
      await this.refreshSession();
      return true;
    } catch (error) {
      return error instanceof AuthError && error.status === 429;
    }
  }

  refreshSession(): Promise<AuthSession> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const cooldownRemainingMs = this.refreshCooldownUntilMonotonicMs - this.monotonicNow();
    if (cooldownRemainingMs > 0) {
      return Promise.reject(new AuthError(
        'Session renewal is temporarily throttled.',
        429,
        'RefreshThrottled',
        undefined,
        {
          method: 'POST',
          requestUrl: buildApiUrl(REFRESH_PATH),
          retryAfterSeconds: Math.ceil(cooldownRemainingMs / 1000),
        },
      ));
    }

    this.refreshPromise = this.refreshSessionCore()
      .then((session) => {
        this.refreshCooldownUntilMonotonicMs = 0;
        return session;
      })
      .catch((error: unknown) => {
        if (error instanceof AuthError && error.status === 429) {
          const retryAfterSeconds = Math.min(
            MAX_REFRESH_COOLDOWN_SECONDS,
            Math.max(1, error.retryAfterSeconds ?? DEFAULT_REFRESH_COOLDOWN_SECONDS),
          );
          this.refreshCooldownUntilMonotonicMs = this.monotonicNow() + retryAfterSeconds * 1000;
        }

        throw error;
      })
      .finally(() => {
        this.refreshPromise = null;
      });
    return this.refreshPromise;
  }

  synchronizeCurrentUserPreferences(session: AuthSession): Observable<CurrentUserResponse | null> {
    if (!this.isCurrentSession(session)) {
      return of(null);
    }

    const sessionKey = this.getSessionKey(session);
    if (this.preferencesSynchronization?.sessionKey === sessionKey) {
      return this.preferencesSynchronization.result;
    }

    const result = this.getCurrentUser().pipe(
      switchMap((currentUser) => {
        if (!this.isCurrentSession(session)) {
          return of(null);
        }

        const preferredLanguageCode = normalizeAppLanguageCode(currentUser.preferredLanguageCode);
        const applyLanguage = preferredLanguageCode
          ? from(this.appLanguageService.useProfileLanguage(preferredLanguageCode)).pipe(
              map(() => true),
              catchError(() => of(false)),
            )
          : of(true);

        return applyLanguage.pipe(
          map((languageApplied) => {
            if (!languageApplied && this.preferencesSynchronization?.sessionKey === sessionKey) {
              this.preferencesSynchronization = null;
            }
            if (!this.isCurrentSession(session)) {
              return null;
            }

            this.regionalPreferencesService.setFromCurrentUser(currentUser);
            return currentUser;
          }),
        );
      }),
      catchError(() => {
        if (this.preferencesSynchronization?.sessionKey === sessionKey) {
          this.preferencesSynchronization = null;
        }
        if (this.isCurrentSession(session)) {
          this.regionalPreferencesService.setFromCurrentUser(null);
        }
        return of(null);
      }),
      shareReplay({bufferSize: 1, refCount: false}),
    );

    this.preferencesSynchronization = {sessionKey, result};
    return result;
  }

  private buildJsonHeaders(): Record<string, string> {
    return {
      'Content-Type': JSON_CONTENT_TYPE,
    };
  }

  private postAuth<T>(path: string, body: unknown): Observable<ApiResponse<T>> {
    const requestUrl = buildApiUrl(path);
    return this.httpClient.post<ApiResponse<T>>(requestUrl, body, {
      headers: this.buildJsonHeaders(),
      context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, authLocalErrorCodes[path] ?? []),
      withCredentials: true,
    }).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          const payload = this.asApiResponse<T>(error.error);
          return throwError(() => this.buildAuthError(payload, error.status, {
            method: 'POST',
            requestUrl,
            responseUrl: error.url ?? undefined,
            statusText: error.statusText,
            httpMessage: error.message,
            retryAfterSeconds: readRetryAfterSeconds(error),
          }, error));
        }

        return throwError(() => error);
      }),
    );
  }

  private getAuth<T>(path: string): Observable<ApiResponse<T>> {
    const requestUrl = buildApiUrl(path);
    return this.httpClient.get<ApiResponse<T>>(requestUrl, {
      headers: this.buildJsonHeaders(),
    }).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          const payload = this.asApiResponse<T>(error.error);
          return throwError(() => this.buildAuthError(payload, error.status, {
            method: 'GET',
            requestUrl,
            responseUrl: error.url ?? undefined,
            statusText: error.statusText,
            httpMessage: error.message,
          }, error));
        }

        return throwError(() => error);
      }),
    );
  }

  private deleteAuth<T>(path: string): Observable<ApiResponse<T>> {
    const requestUrl = buildApiUrl(path);
    return this.httpClient.delete<ApiResponse<T>>(requestUrl, {
      headers: this.buildJsonHeaders(),
    }).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          const payload = this.asApiResponse<T>(error.error);
          return throwError(() => this.buildAuthError(payload, error.status, {
            method: 'DELETE',
            requestUrl,
            responseUrl: error.url ?? undefined,
            statusText: error.statusText,
            httpMessage: error.message,
          }, error));
        }

        return throwError(() => error);
      }),
    );
  }

  private putAuth<T>(path: string, body: unknown): Observable<ApiResponse<T>> {
    const requestUrl = buildApiUrl(path);
    return this.httpClient.put<ApiResponse<T>>(requestUrl, body, {
      headers: this.buildJsonHeaders(),
    }).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          const payload = this.asApiResponse<T>(error.error);
          return throwError(() => this.buildAuthError(payload, error.status, {
            method: 'PUT',
            requestUrl,
            responseUrl: error.url ?? undefined,
            statusText: error.statusText,
            httpMessage: error.message,
          }, error));
        }

        return throwError(() => error);
      }),
    );
  }

  private requireSuccess<T>(payload: ApiResponse<T> | null, status: number): Observable<T> {
    if (!payload?.success || !payload.data) {
      return throwError(() => this.buildAuthError(payload, status));
    }

    return of(payload.data);
  }

  private buildAuthError(
    payload: ApiResponse<unknown> | null,
    status: number,
    diagnostics?: AuthRequestDiagnostics,
    originalError?: unknown,
  ): AuthError {
    if (wasSystemApiErrorPresented(originalError)) {
      return new AuthError('', status, undefined, undefined, diagnostics);
    }
    if (status === 0) {
      return new AuthError(
        `Cannot reach the API at ${environment.apiBaseUrl}. Check the phone connection, VPN, server availability, and CORS access for the Capacitor WebView origin.`,
        status,
        'NetworkError',
        extractDetails(payload),
        diagnostics,
      );
    }

    return new AuthError(
      this.getErrorMessage(
        payload,
        status >= 500 ? '' : `Request failed (${status})`,
      ),
      status,
      payload?.error?.code,
      extractDetails(payload),
      diagnostics,
    );
  }

  private asApiResponse<T>(value: unknown): ApiResponse<T> | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    return value as ApiResponse<T>;
  }

  private storeSessionAndSynchronizePreferences(response: LoginResponse): Observable<AuthSession> {
    return from(this.persistSession(toAuthSession(response))).pipe(
      switchMap((session) => this.synchronizeCurrentUserPreferences(session).pipe(map(() => session))),
    );
  }

  private async refreshSessionCore(): Promise<AuthSession> {
    const refreshToken = await this.readNativeRefreshTokenSafely();
    const request: RefreshTokenRequestDto = {refreshToken};
    const payload = await firstValueFrom(this.postAuth<LoginResponse>(REFRESH_PATH, request));
    const response = await firstValueFrom(this.requireSuccess(payload, 200));
    return this.persistSession(toAuthSession(response));
  }

  private async persistSession(session: AuthSession): Promise<AuthSession> {
    if (isPushSupported()) await this.pushLocalState.prepareAccount(session.user.id);
    const normalizedSession = normalizeSessionTiming(session);
    if (normalizedSession.refreshToken) {
      await this.refreshTokenStorage.write(normalizedSession.refreshToken);
    }

    const persistedSession: AuthSession = {
      accessToken: normalizedSession.accessToken,
      accessTokenRefreshAtEpochMs: normalizedSession.accessTokenRefreshAtEpochMs,
      expiresAt: normalizedSession.expiresAt,
      expiresInSeconds: normalizedSession.expiresInSeconds,
      refreshTokenExpiresAt: normalizedSession.refreshTokenExpiresAt,
      user: normalizedSession.user,
    };
    if (this.preferencesSynchronization?.sessionKey !== this.getSessionKey(persistedSession)) {
      this.preferencesSynchronization = null;
    }
    writeStoredAuthSession(persistedSession);
    this.sessionSubject.next(persistedSession);
    void this.androidVersionObservation.observe(persistedSession);
    return normalizedSession;
  }

  private monotonicNow(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  private async readNativeRefreshTokenSafely(): Promise<string | null> {
    return this.refreshTokenStorage.read();
  }

  private resolveClientType(): AuthClientTypeDto {
    if (isNativeCapacitorPlatform()) {
      return 'Native';
    }

    if (typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('nativeGoogleAuth') === '1') {
      return 'Native';
    }

    return 'Web';
  }

  private clientHistoryMetadata(): {clientPlatform: 'Android' | 'Web'; clientVersion: string} {
    return {
      clientPlatform: isNativeCapacitorPlatform() ? 'Android' : 'Web',
      clientVersion: environment.appVersion,
    };
  }

  private getSessionKey(session: AuthSession): string {
    return `${session.user.id}:${session.accessToken}`;
  }

  private isCurrentSession(session: AuthSession): boolean {
    return !!this.session
      && this.session.user.id === session.user.id
      && this.session.accessToken === session.accessToken;
  }

  private getErrorMessage(payload: ApiResponse<unknown> | null, fallback: string): string {
    const code = payload?.error?.code;
    switch (code) {
      case 'EmailConfirmationRequired':
        return this.translateService.instant('auth.errors.emailConfirmationRequired');
      case 'InvalidCredentials':
        return this.translateService.instant('auth.errors.invalidCredentials');
      case 'InactiveUser':
        return this.translateService.instant('auth.errors.inactiveUser');
      case 'UserEmailAlreadyExists':
        return this.translateService.instant('auth.register.emailAlreadyExistsMessage');
      case 'UserEmailAlreadyExistsInactive':
        return this.translateService.instant('auth.register.emailAlreadyExistsInactiveMessage');
      case 'UsernameAlreadyExists':
        return this.translateService.instant('auth.register.usernameAlreadyExistsMessage');
      default:
        break;
    }

    const message = payload?.error?.message?.trim();
    if (message) {
      return message;
    }

    return fallback;
  }

}
